import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";

import {
  Button,
  Checkbox,
  Dropdown,
  Form,
  FormField,
  FormGroup,
  Input,
  Radio,
  SemanticWIDTHS,
} from "semantic-ui-react";

import ModSearchLocationMap, { ConfirmLocationHandler } from "./ModSearchLocationMap";
import { OfficialAccountAddressDataType } from "./OfficialAccountAddressForm";
import { backgroundClip } from "html2canvas/dist/types/css/property-descriptors/background-clip";

type AccountingAddressFormProps = {
  data: OfficialAccountAddressDataType;
  error?: Record<string, any>;
  readOnly?: boolean;
  width?: SemanticWIDTHS;
  masterData?: Partial<Record<string, any[]>>;
  masterOptions?: Partial<Record<string, any[]>>;
  onChangeValue?: (e: any, data: any) => void;
  onChangeLogoImage?: (file: File, fileUrl: string) => void;
  requiredTextError?: (isRequired: boolean) => React.ReactNode;
  languageUX?: "en" | "th";
};

const AccountingAddressForm = (props: AccountingAddressFormProps) => {
  const fileUploaderRef = useRef();
  const [openModMapLocation, setOpenModMapLocation] = useState<boolean>(false);
  const [isCurrentLocation, setIsCurrentLocation] = useState<boolean>(false);
  const [imageName, setImageName] = useState("No file chosen");

  const getFileName = (url: string) => {
    const pathname = new URL(url).pathname;
    return pathname.split("/").pop();
  };

  useEffect(() => {
    const imageFile = props.data.accounting?.accounting_image_file;

    if (!imageFile) return;

    if (typeof imageFile === "string") {
      const fileName = getFileName(imageFile);
      setImageName(fileName || "No file chosen");
      return;
    }

    if (imageFile instanceof File) {
      setImageName(imageFile.name || "No file chosen");
      return;
    }
  }, [props.data.accounting?.accounting_image_file]);

  const handleChangeProvince = (e: any, data: any) => {
    props.onChangeValue?.(null, { name: "accounting.accounting_province", value: data.value });
    props.onChangeValue?.(null, { name: "accounting.accounting_district", value: "" });
    props.onChangeValue?.(null, { name: "accounting.accounting_city", value: "" });
    props.onChangeValue?.(null, { name: "accounting.accounting_zipcode", value: "" });

    handleResetLocation();
  };

  const handleChangeDistrict = (e: any, data: any) => {
    props.onChangeValue?.(null, { name: "accounting.accounting_district", value: data.value });
    props.onChangeValue?.(null, { name: "accounting.accounting_city", value: "" });
    props.onChangeValue?.(null, { name: "accounting.accounting_zipcode", value: "" });

    handleResetLocation();
  };

  const handleChangeCity = (e: any, data: any) => {
    const foundCity = (props.masterData?.city ?? []).find((item) => item.id === data.value);

    props.onChangeValue?.(null, { name: "accounting.accounting_city", value: data.value });
    props.onChangeValue?.(null, {
      name: "accounting.accounting_zipcode",
      value: foundCity?.zipcode || "",
    });

    handleResetLocation();
  };

  const handleResetLocation = () => {
    props.onChangeValue?.(null, { name: "accounting.latitude", value: "" });
    props.onChangeValue?.(null, { name: "accounting.longitude", value: "" });
    props.onChangeValue?.(null, { name: "accounting.place_id", value: "" });
  };

  const handleOpenModMapLocation = () => {
    if (!props.readOnly) {
      setOpenModMapLocation(true);
    }
  };

  const handleCloseModMapLocation = () => {
    setOpenModMapLocation(false);
    setIsCurrentLocation(false);
  };

  const handleConfirmLocation: ConfirmLocationHandler = (locationData) => {
    props.onChangeValue?.(null, {
      name: "accounting.accounting_province",
      value: locationData.province,
    });
    props.onChangeValue?.(null, {
      name: "accounting.accounting_district",
      value: locationData.district,
    });
    props.onChangeValue?.(null, { name: "accounting.accounting_city", value: locationData.city });
    props.onChangeValue?.(null, {
      name: "accounting.accounting_zipcode",
      value: locationData.zipcode,
    });
    props.onChangeValue?.(null, {
      name: "accounting.accounting_address_text",
      value: locationData.address_text,
    });
    props.onChangeValue?.(null, {
      name: "accounting.latitude",
      value: locationData.latitude,
    });
    props.onChangeValue?.(null, {
      name: "accounting.longitude",
      value: locationData.longitude,
    });
    props.onChangeValue?.(null, {
      name: "accounting.place_id",
      value: locationData.place_id,
    });

    console.log("xxxx", locationData);

    handleCloseModMapLocation();
  };

  const districtOptions = useMemo(() => {
    if (!props.data.accounting?.accounting_province) {
      return [];
    }

    return (props.masterData?.district ?? [])
      .filter((d: any) => d.area === props.data.accounting?.accounting_province)
      .map((d: any) => ({
        key: d.id,
        text: d.name,
        value: d.id,
      }));
  }, [props.data.accounting?.accounting_province, props.masterData?.district]);

  const cityOptions = useMemo(() => {
    if (!props.data.accounting?.accounting_district) {
      return [];
    }

    return (props.masterData?.city ?? [])
      .filter((c: any) => c.area === props.data.accounting?.accounting_district)
      .map((c: any) => ({
        key: c.id,
        text: c.name,
        value: c.id,
      }));
  }, [props.data.accounting?.accounting_district, props.masterData?.city]);

  //UploadImage
  const readChooseFile = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    const fileUrl = URL.createObjectURL(file);

    setImageName(file.name);
    props.onChangeLogoImage(file, fileUrl);
  };

  return (
    <>
      <FormGroup style={{ alignItems: "flex-start" }} inline>
        {!!props.width && (
          <FormField width={props.width} required>
            <label style={{ marginTop: "1.8rem" }}>สถานที่ตั้ง</label>
          </FormField>
        )}
        <FormField
          style={{ alignItems: "flex-start" }}
          width={(16 - Number(props.width || "")) as SemanticWIDTHS}
          inline
          required
        >
          {!props.width && <label style={{ marginTop: "2rem" }}>สถานที่ตั้ง</label>}
          <Form style={{ paddingLeft: "0.5rem", width: "100%" }}>
            <FormGroup style={{ alignItems: "flex-start" }}>
              <FormField
                style={{ display: "grid" }}
                error={props.error?.accounting_address_text ?? false}
                width={10}
                required
              >
                <label style={{ fontSize: ".93em", minWidth: "auto" }}>ที่อยู่</label>
                <Input
                  name="accounting.accounting_address_text"
                  readOnly={props.readOnly}
                  value={props.data.accounting?.accounting_address_text || ""}
                  fluid
                  onChange={props.onChangeValue}
                />
                {props.requiredTextError?.(props.error?.accounting_address_text)}
              </FormField>
              <FormField>
                <img
                  style={{ cursor: props.readOnly ? "" : "pointer", width: "145px" }}
                  alt="find-location-map.icon"
                  src="/static/images/icon-find-location-map.png"
                  onClick={handleOpenModMapLocation}
                />
              </FormField>
            </FormGroup>
            <FormGroup style={{ marginBottom: 0 }}>
              <FormField
                style={{ display: "grid" }}
                error={props.error?.accounting_province ?? false}
                width={4}
                required
              >
                <label style={{ fontSize: ".93em", minWidth: "auto" }}>จังหวัด</label>
                <Dropdown
                  className="inline-label"
                  style={{ width: "100%" }}
                  disabled={props.readOnly}
                  placeholder="-- กรุณาเลือกจังหวัด --"
                  value={props.data.accounting?.accounting_province || ""}
                  fluid
                  search
                  selection
                  options={props.masterOptions?.province}
                  onChange={handleChangeProvince}
                />
              </FormField>
              <FormField
                style={{ display: "grid" }}
                error={props.error?.accounting_district ?? false}
                width={3}
                required
              >
                <label style={{ fontSize: ".93em", minWidth: "auto" }}>เขต /อำเภอ</label>
                <Dropdown
                  className="inline-label"
                  style={{ width: "100%" }}
                  disabled={!props.data.accounting?.accounting_province || props.readOnly}
                  placeholder="-- กรุณาเลือกเขต /อำเภอ --"
                  value={props.data.accounting?.accounting_district || ""}
                  fluid
                  search
                  selection
                  options={districtOptions}
                  onChange={handleChangeDistrict}
                />
              </FormField>
              <FormField
                style={{ display: "grid" }}
                error={props.error?.accounting_city ?? false}
                width={3}
                required
              >
                <label style={{ fontSize: ".93em", minWidth: "auto" }}>แขวง /ตำบล</label>
                <Dropdown
                  className="inline-label"
                  style={{ width: "100%" }}
                  placeholder="-- กรุณาเลือกแขวง /ตำบล --"
                  value={props.data.accounting?.accounting_city || ""}
                  fluid
                  search
                  selection
                  options={cityOptions}
                  onChange={handleChangeCity}
                  disabled={
                    !props.data.accounting?.accounting_province ||
                    !props.data.accounting.accounting_district ||
                    props.readOnly
                  }
                />
              </FormField>
              <FormField style={{ display: "grid" }} width={3}>
                <label style={{ fontSize: ".93em", minWidth: "auto" }}>รหัสไปรษณีย์</label>
                <Input value={props.data.accounting?.accounting_zipcode || ""} fluid readOnly />
              </FormField>
            </FormGroup>
            <FormGroup style={{ marginBottom: 0 }}>
              {props.requiredTextError?.(
                props.error?.accounting_province ||
                  props.error?.accounting_district ||
                  props.error?.accounting_city,
              )}
            </FormGroup>
          </Form>
        </FormField>
      </FormGroup>
      <FormGroup inline>
        <FormField width={props.width} required>
          <label>{"Logo Official Account"}</label>
        </FormField>

        <FormField width={props.width ? 8 : 11}>
          <div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Button
                size="mini"
                style={{
                  background: "var(--primary-theme-color)",
                  color: "white",
                }}
                onClick={() => {
                  fileUploaderRef.current.click();
                }}
              >
                Choose file
              </Button>
              <input
                type="file"
                id="file"
                ref={fileUploaderRef}
                style={{ display: "none" }}
                onChange={readChooseFile}
                accept="image/*"
              />
              <div style={{ fontWeight: "bold" }}>{imageName}</div>
            </div>
            {props.requiredTextError?.(props.error?.accounting_image_file)}
          </div>
        </FormField>
      </FormGroup>

      <ModSearchLocationMap
        editAddress={props.data.division_address}
        isCurrentLocation={isCurrentLocation}
        open={openModMapLocation}
        masterData={props.masterData}
        onClose={handleCloseModMapLocation}
        onConfirm={handleConfirmLocation}
      />
    </>
  );
};

AccountingAddressForm.displayName = "AccountingAddressForm";

export default React.memo(AccountingAddressForm);
